// Direct apiv2 `clinical_<entity>` CRUD over the coordinator SESSION (via ws()),
// replacing the WF50 webhook transport for the back-office. The session path is
// NOT RSA-signed (ws() carries the session cookie), so this ports only WF50's
// request-mapping + response-normalization — never its signing.
//
// SOURCE OF TRUTH for MAP: api/workflows-target/50-apiv2-crud-adapter.json
// (Route & Map + Normalize apiv2). WF50 is frozen for the browser after this
// cutover; keep MAP in sync with WF50 by hand until WF50-for-browser is retired.
// respMaps carry only the id_dev_user→withings_user_id alias; every other field
// is read under apiv2's own emitted name (care_period_id, id, cms_submission_id,
// id_patient, first_name…) — do not re-add a rename apiv2 doesn't need.
//
// Only services/endpoints.ts import this (like crud* before it). Errors are
// re-wrapped to the PlatformApiError shape so consumers branching on `.code` work.

import { ws, WsError } from './wiservice';

// Error shape consumers branch on (`.code`).
export interface PlatformApiError extends Error {
  code?: string;
  fields?: unknown;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- JS-interop dialect data; see header
type Any = any;

// Fetch wide when an adapter-side postFilter is active, then re-slice to the caller's limit.
const POSTFILTER_FETCH_LIMIT = 500;

interface PostFilterDef { field: string; op: 'eq' | 'gte' | 'lte'; }
interface ListCfg { action?: string; queryMap: Record<string, string | null>; postFilter?: Record<string, PostFilterDef>; }
interface WriteCfg { action?: string; idParam?: string; reqMap: Record<string, string | null>; }
interface EntityCfg {
  service: string;
  bodyMode?: 'passthrough';
  list?: ListCfg;
  create?: WriteCfg;
  update?: WriteCfg;
  respMap?: Record<string, string>;
  numericStr?: string[];
}

// Subset of WF50's MAP — the 11 entities the back-office reads/writes.
const MAP: Record<string, EntityCfg> = {
  patients: {
    service: 'clinical_patient',
    list: {
      queryMap: {
        withings_user_id: 'clinical_id_dev_user', email: 'clinical_email', ehr_patient_id: 'clinical_ehr_patient_id', fields: 'fields', sort: 'sort', order: 'clinical_order', offset: 'offset', limit: 'limit',
      },
    },
    respMap: { id_dev_user: 'withings_user_id' },
  },
  'care-plans': {
    service: 'clinical_care_plan',
    list: {
      queryMap: {
        withings_user_id: 'clinical_id_dev_user', status: 'clinical_status', is_current: null, include_goals: null, include_history: null, sort: 'clinical_sort', order: null, offset: 'offset', limit: 'limit',
      },
    },
    respMap: { id_dev_user: 'withings_user_id' },
  },
  'care-periods': {
    service: 'clinical_care_period',
    list: {
      queryMap: {
        withings_user_id: 'clinical_id_dev_user', care_period_id: 'clinical_id_care_period', programid: 'clinical_programid', status: 'clinical_status', has_care_initiation: null, fields: 'fields', sort: 'sort', order: null, offset: 'offset', limit: 'limit',
      },
    },
    respMap: { id_dev_user: 'withings_user_id' },
  },
  'task-instances': {
    service: 'clinical_task_instance',
    list: {
      queryMap: {
        withings_user_id: 'clinical_id_dev_user', care_period_id: 'clinical_id_care_period', task_id: 'clinical_id_task_instance', status: 'clinical_status', template_code: 'clinical_template_code', owner_role: 'clinical_owner_role', visible_to_patient: 'clinical_visible_to_patient', assigned_to_type: 'clinical_assigned_to_type', priority: 'clinical_priority', clinical_domain: null, due_after: 'clinical_due_after', due_before: null, include_template: null, sort: 'sort', order: 'clinical_order', limit: 'limit', offset: 'offset',
      },
      postFilter: { dedupe_key: { field: 'dedupe_key', op: 'eq' } },
    },
    create: {
      reqMap: {
        care_period_id: 'clinical_id_care_period', withings_user_id: 'clinical_id_dev_user', dedupe_key: 'clinical_dedupe_key', task_type: 'clinical_task_type', title: 'clinical_title', description: 'clinical_description', trigger_source: 'clinical_trigger_source', assigned_to_type: 'clinical_assigned_to_type', assigned_to_id: 'clinical_assigned_to_id_dev_user', owner_role: 'clinical_owner_role', task_template_id: 'clinical_id_task_template', template_code: 'clinical_template_code', clinical_domain: 'clinical_clinical_domain', measure_type: 'clinical_measure_type', priority: 'clinical_priority', status: 'clinical_status', visible_to_patient: 'clinical_visible_to_patient', due_at: 'clinical_due_at', available_at: 'clinical_available_at', trigger_ref_type: 'clinical_trigger_ref_type', trigger_ref_id: 'clinical_trigger_ref_id', context_json: 'clinical_context_json', active_flag: null,
      },
    },
    respMap: { id_dev_user: 'withings_user_id' },
  },
  outreach: {
    service: 'clinical_outreach',
    list: {
      queryMap: {
        withings_user_id: 'clinical_id_dev_user', msg_type: 'clinical_msg_type', channel: 'clinical_channel', direction: 'clinical_direction', appli: 'clinical_appli', sort: 'clinical_sort', order: null, offset: 'offset', limit: 'limit',
      },
      postFilter: {
        source: { field: 'source', op: 'eq' }, trigger_source: { field: 'trigger_source', op: 'eq' }, visible_to_patient: { field: 'visible_to_patient', op: 'eq' }, sent_after: { field: 'sent_at', op: 'gte' }, sent_before: { field: 'sent_at', op: 'lte' },
      },
    },
    respMap: { id_dev_user: 'withings_user_id' },
  },
  'measure-states': {
    service: 'clinical_measure_state',
    list: {
      queryMap: {
        care_period_id: 'clinical_id_care_period', withings_user_id: 'clinical_id_dev_user', care_period_status: 'clinical_care_period_status', last_received_before: 'clinical_last_received_before', measure_type: 'clinical_measure_type', status: 'clinical_status', sort: 'clinical_sort', order: null, offset: 'offset', limit: 'limit',
      },
    },
    respMap: { id_dev_user: 'withings_user_id' },
    numericStr: ['baseline_value', 'latest_value'],
  },
  'care-goals': {
    service: 'clinical_care_goal',
    list: {
      queryMap: {
        care_period_id: 'clinical_id_care_period', withings_user_id: 'clinical_id_dev_user', measure_type: 'clinical_measure_type', status: 'clinical_status', sort: 'clinical_sort', order: null, offset: 'offset', limit: 'limit',
      },
    },
    respMap: { id_dev_user: 'withings_user_id' },
    numericStr: ['target_value_num', 'baseline_value_num', 'last_value_num'],
  },
  'cms-submissions': {
    service: 'clinical_cms_submission',
    list: {
      queryMap: {
        withings_user_id: 'clinical_id_dev_user', care_period_id: 'clinical_id_care_period', correlation_id: 'clinical_correlation_id', operation: 'clinical_operation', poll_due: 'clinical_poll_due', status: 'clinical_status', limit: 'limit', offset: 'offset',
      },
    },
    respMap: { id_dev_user: 'withings_user_id' },
  },
  'content-catalog': {
    service: 'clinical_content_catalog',
    list: {
      queryMap: {
        catalog_id: 'clinical_id_content_catalog', track: 'clinical_track', active: 'clinical_active', conditions: 'clinical_conditions', format: null, slug: 'clinical_slug', difficulty: null, theme_family: null, source_org: null, sort: 'sort', order: 'clinical_order', offset: 'offset', limit: 'limit',
      },
      postFilter: { phase: { field: 'phase', op: 'eq' } },
    },
    respMap: {},
  },
  'patient-intakes': {
    service: 'clinical_patient_intake',
    list: {
      queryMap: {
        withings_user_id: 'clinical_id_dev_user', intake_type: 'clinical_intake_type', patient_id: 'clinical_id_patient', submitted_after: 'clinical_submitted_after', submitted_before: 'clinical_submitted_before', offset: 'offset', limit: 'limit',
      },
    },
    respMap: { id_dev_user: 'withings_user_id' },
  },
  'care-pathway-presets': {
    service: 'clinical_care_pathway_preset',
    list: {
      // get filters per apiv2 doc: id is `clinical_id_care_pathway_preset`
      // (NOT clinical_preset_id, that's create-only); `clinical_kind` is supported.
      queryMap: {
        preset_id: 'clinical_id_care_pathway_preset', kind: 'clinical_kind', active_only: 'clinical_active_only', active: 'active', condition_slug: 'clinical_condition_slug', sort: 'sort', order: null, offset: 'offset', limit: 'limit',
      },
    },
    respMap: {},
  },
  'engagement-state': {
    service: 'clinical_engagement_state',
    // clinical_id_dev_user is OPTIONAL on apiv2's get: omit it and every row comes
    // back. No limit/offset/sort/programid — roster-read caveats in
    // docs/specs/2026-07-30-engage-conversation-ordering.md.
    // `has_message` is left unmapped on purpose (WF50 has it as null): it filters on
    // last_message_at, which can be null for a patient who has only ever replied —
    // exactly the rows the interaction sort needs.
    list: {
      queryMap: {
        withings_user_id: 'clinical_id_dev_user',
      },
    },
    update: {
      idParam: 'clinical_id_dev_user',
      // No patient_last_seen_at: the back office never writes the patient's read-tick marker.
      reqMap: {
        last_message_at: 'clinical_last_message_at', last_message_type: 'clinical_last_message_type', messages_today: 'clinical_messages_today', messages_today_date: 'clinical_messages_today_date', messages_this_week: 'clinical_messages_this_week', week_start_date: 'clinical_week_start_date', last_reading_at: 'clinical_last_reading_at', careteam_last_seen_at: 'clinical_careteam_last_seen_at', last_inbound_at: 'clinical_last_inbound_at', _increment: null,
      },
    },
    respMap: { id_dev_user: 'withings_user_id' },
  },
};

// Carries `.code` so it matches the PlatformApiError shape consumers branch on.
class AdapterError extends Error {
  constructor(public code: string, message: string) {
    super(message);
    this.name = 'AdapterError';
  }
}

function cfgFor(entity: string): EntityCfg {
  const cfg = MAP[entity];
  if (!cfg) throw new AdapterError('NOT_FOUND', `Unknown entity for apiv2: ${entity}`);
  return cfg;
}

// apiv2 status → PlatformApiError code (mirrors WF50 Normalize apiv2).
function rewrapWsError(e: WsError): PlatformApiError {
  const codeByStatus: Record<number, string> = { 380: 'NOT_FOUND', 503: 'BAD_REQUEST', 277: 'UNAUTHORIZED' };
  const err = new Error(e.message || `apiv2 status ${e.status}`) as PlatformApiError;
  err.code = codeByStatus[e.status] || 'UPSTREAM_ERROR';
  return err;
}

// Drop undefined/null/false; objects/arrays → JSON; bool true → '1'; else String.
// Matches WF50's signable build (apiv2 json_decodes object params; no signature on
// the session path, so plain JSON.stringify suffices — phpEnc was signature-only).
function serialize(raw: Record<string, unknown>): Record<string, string | number> {
  return Object.fromEntries(
    Object.entries(raw)
      .filter(([, v]) => v !== undefined && v !== null && v !== false)
      .map(([k, v]) => {
        if (typeof v === 'object') return [k, JSON.stringify(v)];
        if (typeof v === 'boolean') return [k, '1'];
        if (typeof v === 'number') return [k, v];
        return [k, String(v)];
      }),
  );
}

interface QueryResult { params: Record<string, unknown>; postFilters: Array<PostFilterDef & { value: string }>; origLimit: number | null; }

// Query mapping — null target = apiv2 lacks the filter → throw LOUD (a silently
// dropped read filter widens scope). Arrays → CSV (apiv2 IN-matches). Mirrors WF50 mapQuery.
function mapQuery(entity: string, listCfg: ListCfg, query: Record<string, unknown>): QueryResult {
  const qm = listCfg.queryMap || {};
  const pf = listCfg.postFilter || {};
  const params: Record<string, unknown> = {};
  const postFilters: QueryResult['postFilters'] = [];
  Object.entries(query).forEach(([k, vRaw]) => {
    if (vRaw === undefined || vRaw === null || vRaw === '') return;
    if (pf[k]) { postFilters.push({ ...pf[k], value: String(vRaw) }); return; }
    const target = qm[k];
    if (target === undefined) throw new AdapterError('UNMAPPED_FIELD', `Query param not in adapter map for ${entity}: ${k}`);
    if (target === null) throw new AdapterError('UNSUPPORTED_FILTER', `apiv2 has no equivalent for filter ${entity}?${k}`);
    const v = Array.isArray(vRaw) ? vRaw.join(',') : vRaw;
    if (v === 'true' || v === true) params[target] = '1';
    else if (v === 'false' || v === false) params[target] = '0';
    else params[target] = v;
  });
  let origLimit: number | null = null;
  if (postFilters.length) {
    origLimit = params.limit !== undefined ? Number(params.limit) : null;
    params.limit = POSTFILTER_FETCH_LIMIT;
  }
  return { params, postFilters, origLimit };
}

// Body mapping — throw UNMAPPED_FIELD on an unknown field unless bodyMode=passthrough.
// priority enum→int. Mirrors WF50 mapBody.
function mapBody(
  entity: string,
  cfg: EntityCfg,
  reqMap: Record<string, string | null>,
  obj: Record<string, unknown>,
): Record<string, unknown> {
  const params: Record<string, unknown> = {};
  Object.entries(obj).forEach(([k, v]) => {
    if (k in reqMap) {
      const target = reqMap[k];
      if (target == null) return;
      // Forward the value verbatim — matching WF50. NB `priority` stays the string
      // enum ('normal'|'high'|…): the task_instance.priority column is TEXT, not int.
      // (WF50's enum→int branch is dead code — its reqMap maps priority→clinical_priority,
      // never the bare 'priority' it gates on — so it too forwards the string.)
      params[target] = v;
    } else if (cfg.bodyMode === 'passthrough') {
      if (k === 'withings_user_id') params.clinical_id_dev_user = v;
      else params[k.startsWith('clinical_') ? k : `clinical_${k}`] = v;
    } else {
      throw new AdapterError('UNMAPPED_FIELD', `Field not in adapter map for ${entity}: ${k}`);
    }
  });
  return params;
}

function renameRow(row: Any, respMap: Record<string, string>, numericStr: string[]): Any {
  if (!row || typeof row !== 'object' || Array.isArray(row)) return row;
  const o: Record<string, unknown> = {};
  Object.entries(row).forEach(([k, v]) => { o[respMap[k] || k] = v; });
  numericStr.forEach((f) => {
    if (o[f] !== undefined && o[f] !== null && typeof o[f] === 'number') o[f] = String(o[f]);
  });
  return o;
}

function applyPostFilters(data: Any[], postFilters: QueryResult['postFilters'], origLimit: number | null): Any[] {
  let out = data;
  postFilters.forEach((f) => {
    out = out.filter((row: Any) => {
      const rv = row[f.field];
      if (rv === undefined || rv === null) return false;
      if (f.op === 'eq') return String(rv) === f.value || (f.value === 'true' && rv === true) || (f.value === 'false' && rv === false);
      if (f.op === 'gte') return String(rv) >= f.value;
      if (f.op === 'lte') return String(rv) <= f.value;
      return true;
    });
  });
  if (origLimit !== null && Number.isFinite(origLimit)) out = out.slice(0, origLimit);
  return out;
}

// ws() throws WsError on a non-zero apiv2 status; re-wrap to PlatformApiError shape.
async function callApiv2(service: string, action: string, params: Record<string, unknown>): Promise<Any> {
  try {
    const res = await ws<Any>(service, action, serialize(params));
    return res.body ?? {};
  } catch (e) {
    if (e instanceof WsError) throw rewrapWsError(e);
    if (e && typeof e === 'object' && 'code' in e) throw e as unknown as Error; // already PlatformApiError-shaped
    // Transport/network failure (no WsError) → coded error, parity with WF50's UPSTREAM_TIMEOUT.
    const err = new Error((e as Error)?.message || 'apiv2 transport error') as PlatformApiError;
    err.code = 'UPSTREAM_ERROR';
    throw err;
  }
}

function unwrapRows(body: Any, cfg: EntityCfg, alwaysArray: boolean): Any {
  const inner = body && typeof body === 'object' ? body : {};
  // body may be the array directly, the {data} envelope, or (malformed/empty)
  // neither. For a list with no `data` key, return [] — safer than WF50's [inner].
  let data: Any;
  if (Array.isArray(inner)) data = inner;
  else if (inner.data !== undefined) data = inner.data;
  else data = alwaysArray ? [] : inner;
  const respMap = cfg.respMap || {};
  const numericStr = cfg.numericStr || [];
  if (Array.isArray(data)) return data.map((r: Any) => renameRow(r, respMap, numericStr));
  if (data == null) return alwaysArray ? [] : null;
  const renamed = renameRow(data, respMap, numericStr);
  return alwaysArray ? [renamed] : renamed;
}

// ── Public surface (mirrors crud*) ───────────────────────────────────────────

interface ListOutput { data: Any[]; meta: { count?: number } | undefined; }

async function listEntity(entity: string, restParams: Record<string, unknown>): Promise<ListOutput> {
  const cfg = cfgFor(entity);
  if (!cfg.list) throw new AdapterError('NOT_FOUND', `${entity} has no list`);
  const { params, postFilters, origLimit } = mapQuery(entity, cfg.list, restParams || {});
  const body = await callApiv2(cfg.service, cfg.list.action || 'get', params);
  let data: Any[] = unwrapRows(body, cfg, true);
  if (postFilters.length) data = applyPostFilters(data, postFilters, origLimit);
  const innerMeta = body && typeof body === 'object' && body.meta && typeof body.meta === 'object' ? body.meta : null;
  let meta: { count?: number } | undefined;
  if (innerMeta) {
    const m: { count?: number } = { ...innerMeta };
    if (m.count === undefined && innerMeta.total_count !== undefined) m.count = innerMeta.total_count;
    meta = m;
  }
  return { data, meta };
}

/** List read → rows (always an array). */
export async function apiv2Get(entity: string, restParams: Record<string, unknown> = {}): Promise<Any> {
  const { data } = await listEntity(entity, restParams);
  return data;
}

/** List read → { status, data, meta:{count} } envelope. */
export async function apiv2GetEnvelope(entity: string, restParams: Record<string, unknown> = {}): Promise<Any> {
  const { data, meta } = await listEntity(entity, restParams);
  return { status: 'success', data, meta };
}

/** Create → the created row. */
export async function apiv2Create(entity: string, body: Record<string, unknown>): Promise<Any> {
  const cfg = cfgFor(entity);
  if (!cfg.create) throw new AdapterError('NOT_FOUND', `${entity} has no create`);
  const params = mapBody(entity, cfg, cfg.create.reqMap, body || {});
  const out = await callApiv2(cfg.service, cfg.create.action || 'create', params);
  return unwrapRows(out, cfg, false);
}

/** Update by id → the updated row. */
export async function apiv2Update(entity: string, id: string | number, changes: Record<string, unknown>): Promise<Any> {
  const cfg = cfgFor(entity);
  const idParam = cfg.update?.idParam;
  if (!cfg.update || !idParam) throw new AdapterError('NOT_FOUND', `${entity} has no update`);
  const params = mapBody(entity, cfg, cfg.update.reqMap, changes || {});
  params[idParam] = id;
  const out = await callApiv2(cfg.service, cfg.update.action || 'update', params);
  return unwrapRows(out, cfg, false);
}
